import type { CaptureResult, PostHog } from 'posthog-js';

const PUBLIC_PAGES = new Set([
    '/', '/login', '/register', '/verify-registration', '/privacy', '/terms',
    '/app-privacy', '/availability', '/support', '/delete-account', '/forgot-password',
]);
const EVENTS = new Set([
    '$pageview', 'signup_clicked', 'signup_verification_requested', 'workspace_created', 'app_store_clicked',
]);
type AnalyticsEvent = 'signup_clicked' | 'signup_verification_requested' | 'workspace_created' | 'app_store_clicked';
const CAMPAIGNS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const SAFE_PROPERTIES = new Set([
    'token', 'distinct_id', '$cookieless_mode', '$process_person_profile',
    '$current_url', '$pathname', '$host', '$referrer', '$referring_domain',
    // Required for PostHog's daily cookieless hash; stripped by PostHog before storage.
    '$raw_user_agent', '$browser', '$browser_version', '$os', '$os_version', '$device', '$device_type',
    '$screen_height', '$screen_width', '$viewport_height', '$viewport_width',
    '$lib', '$lib_version', '$timezone', '$timezone_offset', ...CAMPAIGNS,
]);
let client: Promise<PostHog | undefined> | undefined;

export function analyticsAllowed(): boolean {
    if (!import.meta.env.PROD || !['kaziatlas.com', 'www.kaziatlas.com'].includes(window.location.hostname)) return false;
    if (navigator.webdriver || navigator.doNotTrack === '1' || (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return false;
    try {
        return localStorage.getItem('ka-analytics-opt-out') !== '1';
    } catch {
        return false;
    }
}

/** Never send query strings, fragments, private routes, form values, or person profiles. */
export function sanitizeAnalyticsEvent(event: CaptureResult | null): CaptureResult | null {
    if (!event || !EVENTS.has(event.event) || !analyticsAllowed()) return null;
    const path = event.properties.$pathname;
    if (typeof path !== 'string' || !PUBLIC_PAGES.has(path)) return null;
    const properties = Object.fromEntries(Object.entries(event.properties).filter(([key]) => SAFE_PROPERTIES.has(key)));
    properties.$current_url = `https://kaziatlas.com${path}`;
    properties.$host = 'kaziatlas.com';
    properties.$process_person_profile = false;
    try {
        const referrer = new URL(String(properties.$referrer));
        properties.$referrer = referrer.origin;
        properties.$referring_domain = referrer.hostname;
    } catch {
        properties.$referrer = '$direct';
        properties.$referring_domain = '$direct';
    }
    for (const key of CAMPAIGNS) {
        const value = properties[key];
        if (typeof value !== 'string' || !/^[a-zA-Z0-9 _.-]{1,100}$/.test(value)) delete properties[key];
    }
    return { uuid: event.uuid, event: event.event, timestamp: event.timestamp, properties };
}

function getClient(): Promise<PostHog | undefined> {
    const token = import.meta.env.VITE_POSTHOG_TOKEN;
    const host = import.meta.env.VITE_POSTHOG_HOST;
    if (!analyticsAllowed() || !token?.startsWith('phc_') || !['https://us.i.posthog.com', 'https://eu.i.posthog.com'].includes(host)) {
        return Promise.resolve(undefined);
    }
    client ??= import('posthog-js').then(({ default: posthog }) => {
        if (!analyticsAllowed()) return undefined;
        posthog.init(token, {
            api_host: host,
            defaults: '2026-05-30',
            cookieless_mode: 'always',
            person_profiles: 'never',
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            capture_dead_clicks: false,
            capture_heatmaps: false,
            capture_performance: false,
            capture_exceptions: false,
            rageclick: false,
            disable_session_recording: true,
            disable_surveys: true,
            disable_web_experiments: true,
            disable_external_dependency_loading: true,
            advanced_disable_flags: true,
            save_referrer: false,
            save_campaign_params: false,
            respect_dnt: true,
            before_send: sanitizeAnalyticsEvent,
        });
        return posthog;
    }).catch(() => undefined); // Analytics must never interrupt navigation or signup.
    return client;
}

function capture(event: string): void {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (!analyticsAllowed() || !PUBLIC_PAGES.has(path)) return;
    const params = new URLSearchParams(window.location.search);
    const properties: Record<string, string> = { $pathname: path, $referrer: document.referrer };
    for (const key of CAMPAIGNS) {
        const value = params.get(key);
        if (value && /^[a-zA-Z0-9 _.-]{1,100}$/.test(value)) properties[key] = value;
    }
    void getClient().then(posthog => {
        if (analyticsAllowed()) posthog?.capture(event, properties);
    }).catch(() => undefined);
}

export function trackPublicPage(): void { capture('$pageview'); }
export function trackAnalyticsEvent(event: AnalyticsEvent): void { capture(event); }
